import React, { useState, useEffect } from 'react';

const ImportProcessingModal = ({ isOpen, onClose, onBack, importType, rawData, onConfirmImport }) => {
  const [processingState, setProcessingState] = useState('processing'); // 'processing', 'editing', 'confirming'
  const [memberData, setMemberData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [duplicates, setDuplicates] = useState([]);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setProcessingState('processing');
      setMemberData([]);
      
      if (rawData) {
        // Simulate processing delay
        setTimeout(() => {
          // Ensure rawData is an array
          const dataArray = Array.isArray(rawData) ? rawData : [];
          const processedData = dataArray.map((member, index) => ({
            ...member,
            id: index,
            name: member?.name || '',
            email: member?.email || '',
            phone: member?.phone || ''
          }));
          setMemberData(processedData);
          
          // Detect duplicates
          const foundDuplicates = detectDuplicates(processedData);
          setDuplicates(foundDuplicates);
          setShowDuplicateAlert(foundDuplicates.length > 0);
          
          setProcessingState('editing');
        }, 1000);
      } else {
        // No data provided, show error state
        setTimeout(() => {
          setProcessingState('editing');
        }, 500);
      }
    }
  }, [isOpen, rawData]);

  if (!isOpen) return null;

  const handleCellEdit = (index, field, value) => {
    setMemberData(prev => prev.map((member, i) => 
      i === index ? { ...member, [field]: value } : member
    ));
  };

  const handleDeleteMember = (index) => {
    setMemberData(prev => prev.filter((_, i) => i !== index));
  };

  // Detect duplicates in member data
  const detectDuplicates = (data) => {
    const duplicates = [];
    const seen = { emails: {}, phones: {} };
    
    data.forEach((member, index) => {
      // Check email duplicates
      if (member.email && member.email.trim()) {
        const email = member.email.toLowerCase().trim();
        if (seen.emails[email]) {
          duplicates.push({
            type: 'email',
            value: member.email,
            indices: [seen.emails[email], index],
            field: 'email'
          });
        } else {
          seen.emails[email] = index;
        }
      }
      
      // Check phone duplicates
      if (member.phone && member.phone.trim()) {
        const phone = member.phone.replace(/[^\d+]/g, ''); // Normalize for comparison
        if (seen.phones[phone]) {
          duplicates.push({
            type: 'phone',
            value: member.phone,
            indices: [seen.phones[phone], index],
            field: 'phone'
          });
        } else {
          seen.phones[phone] = index;
        }
      }
    });
    
    return duplicates;
  };

  // Clear duplicates by removing duplicate values (not entire rows)
  const handleClearDuplicates = () => {
    setMemberData(prev => {
      const updated = [...prev];
      
      duplicates.forEach(duplicate => {
        // Clear the duplicate value from the second occurrence (keep first)
        const secondIndex = duplicate.indices[1];
        if (updated[secondIndex]) {
          updated[secondIndex] = {
            ...updated[secondIndex],
            [duplicate.field]: ''
          };
        }
      });
      
      return updated;
    });
    
    // Hide the alert after clearing
    setShowDuplicateAlert(false);
    setDuplicates([]);
  };

  const handleContinueToConfirm = () => {
    // Validate that all members have phone numbers
    const membersWithoutPhone = memberData.filter(member => !member.phone || !member.phone.trim());
    
    if (membersWithoutPhone.length > 0) {
      alert(`${membersWithoutPhone.length} member(s) are missing phone numbers. Please add phone numbers for all members before continuing.`);
      return;
    }
    
    setProcessingState('confirming');
  };

  const handleBackToEdit = () => {
    setProcessingState('editing');
  };

  const handleConfirmImport = () => {
    onConfirmImport(memberData);
  };

  const renderProcessingView = () => (
    <div className="text-center py-10 px-5">
      <div className="w-10 h-10 border-4 border-gray-200 border-t-blue-500 rounded-full animate-spin mx-auto mb-5"></div>
      <h3 className="text-lg font-semibold text-gray-800 mb-2">Processing your data...</h3>
      <p className="text-gray-600">Please wait while we organize your member information.</p>
    </div>
  );

  const renderEditingView = () => (
    <div className="p-0">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Edit member details ({memberData.length} members)</h3>
      </div>
      
      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
        <table className="w-full bg-white border-collapse">
          <thead>
            <tr>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Name</th>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Email</th>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Phone *</th>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Actions</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(memberData) && memberData.map((member, index) => (
              <tr key={index} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 border-b border-gray-100">
                  <input
                    type="text"
                    value={member.name || ''}
                    onChange={(e) => handleCellEdit(index, 'name', e.target.value)}
                    className="w-full border border-transparent px-2 py-1.5 text-sm bg-transparent rounded focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Enter name"
                  />
                </td>
                <td className="px-3 py-2 border-b border-gray-100">
                  <input
                    type="email"
                    value={member.email || ''}
                    onChange={(e) => handleCellEdit(index, 'email', e.target.value)}
                    className="w-full border border-transparent px-2 py-1.5 text-sm bg-transparent rounded focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Enter email"
                  />
                </td>
                <td className="px-3 py-2 border-b border-gray-100">
                  <input
                    type="tel"
                    value={member.phone || ''}
                    onChange={(e) => handleCellEdit(index, 'phone', e.target.value)}
                    className="w-full border border-transparent px-2 py-1.5 text-sm bg-transparent rounded focus:outline-none focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-200 transition-all"
                    placeholder="Enter phone"
                  />
                </td>
                <td className="px-3 py-2 border-b border-gray-100">
                  <button
                    className="bg-transparent border-none cursor-pointer p-1 rounded hover:bg-red-100 transition-colors"
                    onClick={() => handleDeleteMember(index)}
                    title="Delete member"
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {memberData.length === 0 && (
        <div className="text-center py-10 px-5 text-gray-600">
          <p>No members to import. Please go back and try again.</p>
        </div>
      )}
    </div>
  );

  const renderConfirmingView = () => (
    <div className="p-0">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800">Confirm member details</h3>
      </div>
      
      <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
        <table className="w-full bg-white border-collapse">
          <thead>
            <tr>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Name</th>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Email</th>
              <th className="bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b-2 border-gray-200 sticky top-0 z-10">Phone *</th>
            </tr>
          </thead>
          <tbody>
            {Array.isArray(memberData) && memberData.map((member, index) => (
              <tr key={index} className="hover:bg-gray-50 transition-colors">
                <td className="px-3 py-2 border-b border-gray-100 text-gray-700 text-sm">{member?.name || '—'}</td>
                <td className="px-3 py-2 border-b border-gray-100 text-gray-700 text-sm">{member?.email || '—'}</td>
                <td className="px-3 py-2 border-b border-gray-100 text-gray-700 text-sm">{member?.phone || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] min-h-[70vh] flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Import Members</h2>
          <button 
            className="text-gray-400 hover:text-gray-600 text-3xl font-light leading-none transition-colors"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-6">
          {/* Duplicate Alert */}
          {showDuplicateAlert && (
            <div className="bg-yellow-50 text-yellow-800 border border-yellow-200 rounded-lg px-4 py-3 flex items-center justify-between mb-4 shadow-sm">
              <span className="text-sm font-medium">{duplicates.length} duplicate{duplicates.length > 1 ? 's' : ''} found</span>
              <button 
                className="bg-yellow-700 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-yellow-800 transition-colors"
                onClick={handleClearDuplicates}
              >
                Clear
              </button>
            </div>
          )}
          
          {processingState === 'processing' && renderProcessingView()}
          {processingState === 'editing' && renderEditingView()}
          {processingState === 'confirming' && renderConfirmingView()}
        </div>
        
        <div className="flex items-center justify-between gap-3 p-6 border-t border-gray-200 bg-gray-50">
          {processingState === 'editing' && (
            <>
              <button 
                className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-px"
                onClick={onBack}
              >
                Back
              </button>
              <button 
                className="px-6 py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium shadow-md hover:from-indigo-700 hover:to-purple-700 transition-all hover:-translate-y-px hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                onClick={handleContinueToConfirm}
                disabled={memberData.length === 0}
              >
                Next
              </button>
            </>
          )}
          
          {processingState === 'confirming' && (
            <>
              <button 
                className="px-6 py-2.5 bg-white text-gray-700 border border-gray-300 rounded-lg font-medium hover:bg-gray-50 hover:border-gray-400 transition-all hover:-translate-y-px"
                onClick={handleBackToEdit}
              >
                Back to Edit
              </button>
              <button 
                className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-medium shadow-md hover:from-green-700 hover:to-green-800 transition-all hover:-translate-y-px hover:shadow-lg"
                onClick={handleConfirmImport}
              >
                Import {memberData.length} Member{memberData.length !== 1 ? 's' : ''}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportProcessingModal;